import { useState } from 'react';
import { useModalStore } from '../../stores/modalStore.js';
import { useAeThex } from '../../components/AeThexProvider.jsx';

const TAG_COLORS = {
  question:  '#6366f1', help:    '#0ea5e9', bug:    '#ef4444',
  feature:   '#22c55e', discuss: '#f59e0b', solved: '#10b981',
  wip:       '#8b5cf6', idea:    '#06b6d4',
};

const DEMO_POSTS = [
  { id: 'fp-1', title: 'How do I set up role-based permissions?', author: 'nova', authorAvatar: 'N', tags: ['question', 'help'], replies: 12, reactions: 8, preview: 'I\'m trying to configure roles so that only certain members can see specific channels...', createdAt: Date.now() - 7200000, solved: true },
  { id: 'fp-2', title: 'Feature Request: Custom emoji upload for members', author: 'drift',  authorAvatar: 'D', tags: ['feature', 'idea'],  replies: 5,  reactions: 23, preview: 'Would love to see a feature where regular members can upload their own emoji...', createdAt: Date.now() - 18000000, solved: false },
  { id: 'fp-3', title: 'Bug: Notifications not clearing after reading', author: 'echo',   authorAvatar: 'E', tags: ['bug'],           replies: 3,  reactions: 2,  preview: 'After I open a channel and read all messages, the notification badge sometimes stays...', createdAt: Date.now() - 86400000, solved: false },
  { id: 'fp-4', title: 'Voice channel lag on mobile devices',          author: 'pulse',  authorAvatar: 'P', tags: ['bug', 'help'],   replies: 7,  reactions: 4,  preview: 'When joining a voice channel from my phone I experience significant audio delay...', createdAt: Date.now() - 172800000, solved: true },
  { id: 'fp-5', title: 'General discussion: best practices for server organization', author: 'axiom', authorAvatar: 'A', tags: ['discuss'], replies: 31, reactions: 15, preview: 'Let\'s talk about how you organize your servers. What category structure works best...', createdAt: Date.now() - 259200000, solved: false },
];

function relTime(ts) {
  const d = Date.now() - ts;
  if (d < 3600000) return `${Math.floor(d / 60000)}m ago`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`;
  return `${Math.floor(d / 86400000)}d ago`;
}

function PostCard({ post, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* Avatar */}
        <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0, marginTop: 2 }}>
          {post.authorAvatar || post.author?.[0]?.toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            {post.solved && (
              <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10, background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}>✓ Solved</span>
            )}
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{post.title}</span>
          </div>

          {/* Preview */}
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, marginBottom: 8, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {post.preview}
          </div>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
              {post.tags.map(t => (
                <span key={t} style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${TAG_COLORS[t] || '#6b7280'}18`, color: TAG_COLORS[t] || '#9ca3af', border: `1px solid ${TAG_COLORS[t] || '#6b7280'}33` }}>{t}</span>
              ))}
            </div>
          )}

          {/* Meta row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
            <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>{post.author}</span>
            <span>·</span>
            <span>{relTime(post.createdAt)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>💬 {post.replies}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>❤️ {post.reactions}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewPostDialog({ onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const toggleTag = (t) => setSelectedTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#0f1117', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 14, width: 560, maxWidth: '95vw', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 22px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🗂️ New Post</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Post title…" maxLength={120}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {Object.keys(TAG_COLORS).map(t => (
              <button key={t} onClick={() => toggleTag(t)}
                style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: selectedTags.includes(t) ? `${TAG_COLORS[t]}22` : 'rgba(255,255,255,0.04)', border: `1px solid ${selectedTags.includes(t) ? TAG_COLORS[t] : 'rgba(255,255,255,0.08)'}`, color: selectedTags.includes(t) ? TAG_COLORS[t] : 'rgba(255,255,255,0.4)', transition: 'all 0.12s' }}>{t}</button>
            ))}
          </div>
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Describe your question or topic…" rows={6}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </div>
        <div style={{ padding: '14px 22px', display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 12 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => { if (title.trim()) { onSubmit({ title, body, tags: selectedTags }); onClose(); } }}
            disabled={!title.trim()}
            style={{ padding: '8px 20px', borderRadius: 7, background: title.trim() ? '#6366f1' : 'rgba(99,102,241,0.3)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: title.trim() ? 'pointer' : 'default' }}>
            Post
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ForumView({ channel, serverId }) {
  const [posts, setPosts] = useState(DEMO_POSTS);
  const [showNew, setShowNew] = useState(false);
  const [filterTag, setFilterTag] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const { onOpen } = useModalStore();
  const { user } = useAeThex();

  const filtered = posts
    .filter(p => !filterTag || p.tags?.includes(filterTag))
    .sort((a, b) => sortBy === 'recent' ? b.createdAt - a.createdAt : b.replies - a.replies);

  const handleSubmit = ({ title, body, tags }) => {
    const newPost = {
      id: `fp-${Date.now()}`,
      title,
      preview: body,
      author: user?.username || user?.display_name || 'You',
      authorAvatar: (user?.username || 'Y')[0].toUpperCase(),
      tags,
      replies: 0,
      reactions: 0,
      createdAt: Date.now(),
      solved: false,
    };
    setPosts(p => [newPost, ...p]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {showNew && <NewPostDialog onClose={() => setShowNew(false)} onSubmit={handleSubmit} />}

      {/* Forum header */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginRight: 4 }}>🗂️ Forum</span>

        {/* Tag filter pills */}
        <button onClick={() => setFilterTag('')}
          style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: !filterTag ? '#6366f1' : 'rgba(255,255,255,0.05)', border: `1px solid ${!filterTag ? '#6366f1' : 'rgba(255,255,255,0.08)'}`, color: !filterTag ? '#fff' : 'rgba(255,255,255,0.45)' }}>All</button>
        {['question', 'bug', 'feature', 'discuss'].map(t => (
          <button key={t} onClick={() => setFilterTag(filterTag === t ? '' : t)}
            style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: filterTag === t ? `${TAG_COLORS[t]}22` : 'rgba(255,255,255,0.04)', border: `1px solid ${filterTag === t ? TAG_COLORS[t] : 'rgba(255,255,255,0.07)'}`, color: filterTag === t ? TAG_COLORS[t] : 'rgba(255,255,255,0.4)' }}>{t}</button>
        ))}

        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ marginLeft: 'auto', padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', color: 'rgba(255,255,255,0.55)', fontSize: 11, cursor: 'pointer', outline: 'none' }}>
          <option value="recent">Recent</option>
          <option value="popular">Most Replies</option>
        </select>

        <button onClick={() => setShowNew(true)}
          style={{ padding: '5px 14px', borderRadius: 7, background: '#6366f1', border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + New Post
        </button>
      </div>

      {/* Post list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🗂️</div>
            No posts yet. Be the first to post!
          </div>
        ) : filtered.map(post => (
          <PostCard key={post.id} post={post} onClick={() => onOpen('thread', { message: { id: post.id, text: post.preview, author: post.author, senderName: post.author }, serverId, channelId: channel?.id })} />
        ))}
      </div>
    </div>
  );
}
